import React, { useEffect } from 'react';
import { Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import CheckboxInputField from 'components/layout/forms/CheckboxInputField';
import doorGlassTypes, {
  DOOR_GLASS_1076,
  DOOR_GLASS_1276,
  DOOR_GLASS_876,
} from 'components/configurator/types/doorGlassTypes';
import doorGlassTypesSlidy30 from 'components/configurator/types/doorGlassTypesSlidy30';
import doorGlassTypesSlidy36 from 'components/configurator/types/doorGlassTypesSlidy36';
import getChecked from 'services/forms/getChecked';
import glassWallPartTypes from 'components/configurator/types/glassWallPartTypes';
import handleFirstAndLastDoorSettings from 'components/configurator/services/handleFirstAndLastDoorSettings';
import IGlassWallDoor from 'components/interfaces/IGlassWallDoor';
import NumberInputField from 'components/layout/forms/NumberInputField';
import TextInputField from 'components/layout/forms/TextInputField';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import translations from 'services/translations/translations';
import {
  isAluGlazyDoor,
  isAluGlazyLeafDoorGlassAround,
  isAluGlazyOnlyLeafDoor,
  isAluTypeDoor,
  isOnlyDoorType,
  isSlidyDoor,
  isWoodyTypeDoor,
  SLIDY_ONLY_DOOR_OUTSIDE_GLASS_30,
  SLIDY_ONLY_DOOR_OUTSIDE_GLASS_36,
} from 'components/configurator/types/doorTypes';
import { LEFT, RIGHT } from 'components/configurator/types/doorHangTypes';
import { setWall } from 'store/selectedWall/actions';

import {
  is2kAluGlazy,
  isAluGlazy,
  isSlidy30,
  isSlidy36,
  isWoodyTypeWallWith42and43,
  isWoodyTypeWallWithout42and43,
} from 'components/configurator/types/glazingWallTypesCheck';

import DoorGlasses from './DoorGlasses';
import getDoorPropertiesHandler from './getDoorPropertiesHandler';
import getFieldErrorMessage from '../getFieldErrorMessage';
import handleGlassWallProperties from '../handleGlassWallProperties';
import validateDoor from './validateDoor';
import {
  doorGlassOptions,
  doorHangOptions,
  doorLeafDirectionOptions,
  doorOptions,
  handleTypeOptions,
  thresholdOptions,
  woodDoorHeightOptions,
  woodDoorThresholdOptions,
  woodHingeOptions,
} from '../../../../types/doorOptions';
import IValidationError from '../IValidationError';
import 'style/configurator/Modal.css';
import { HANDLE_TYPE_DEFAULT } from '../../../../types/handleTypes';

import doorGlassTypesAluGlazyLeafGlassAround from '../../../../types/doorGlassTypesAluGlazyLeafGlassAround';

const SelectedDoorProperties = ({
  doors,
  selectedDoor,
  setChangedFirstLastDoor,
  setChangedFirstLastDoorInWall,
  disableInputs,
  validationErrors,
}: {
  doors: IGlassWallDoor[];
  selectedDoor: number;
  setChangedFirstLastDoor: (changed: boolean) => void;
  setChangedFirstLastDoorInWall: (changed: boolean) => void;
  disableInputs?: boolean;
  validationErrors: IValidationError[];
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const wall = useSelector((state: any) => state.selectedWall.properties);
  const { t } = useTranslation([translations.glassWallDoorProperties]);

  const getDoorGlassTypesList = () => {
    const doorToEdit = doors.find(
      (existingDoor): existingDoor is IGlassWallDoor =>
        existingDoor.id === door.id
    );

    if (isSlidy30(wall)) {
      return doorGlassOptions(doorGlassTypesSlidy30);
    }

    if (isSlidy36(wall)) {
      return doorGlassOptions(doorGlassTypesSlidy36);
    }

    if (doorToEdit != null && isAluGlazyLeafDoorGlassAround(doorToEdit)) {
      return doorGlassOptions(doorGlassTypesAluGlazyLeafGlassAround);
    }

    return doorGlassOptions(doorGlassTypes);
  };

  const updateDoor = (doorId: number, updatedDoor: IGlassWallDoor) => {
    handleGlassWallProperties({
      target: {
        name: 'parts',
        value: wall.parts.map((part: any) =>
          part.type === glassWallPartTypes.door && part.id === doorId
            ? updatedDoor
            : part
        ),
      },
    });
  };

  const handleDoorProperties = getDoorPropertiesHandler(
    doors,
    setChangedFirstLastDoor,
    setChangedFirstLastDoorInWall
  );

  const handleWallProperties = (e: any) => {
    handleGlassWallProperties({
      target: {
        name: e.target.name,
        value: e.target.value,
      },
    });
  };

  const handleDoorTypeChange = (doorId: number) => (e: any) => {
    const doorToEdit = doors.find(
      (existingDoor): existingDoor is IGlassWallDoor =>
        existingDoor.id === doorId
    );

    if (doorToEdit === undefined) {
      return;
    }

    const updatedDoor = {
      ...doorToEdit,
      typeName: e.target.value,
    };

    if (isSlidyDoor(updatedDoor)) {
      if (updatedDoor.isFirstDoor) {
        updatedDoor.doorHang = RIGHT;
      }

      if (updatedDoor.isLastDoor) {
        updatedDoor.doorHang = LEFT;
      }

      if (isOnlyDoorType(updatedDoor)) {
        updatedDoor.doorOpeningHeight = 0;
      }

      if (isSlidy30(wall) && isOnlyDoorType(updatedDoor)) {
        updatedDoor.doorGlassType = DOOR_GLASS_876;
      }

      if (isSlidy36(wall) && isOnlyDoorType(updatedDoor)) {
        updatedDoor.doorGlassType = DOOR_GLASS_1276;
      }
    }

    if (isWoodyTypeDoor(updatedDoor)) {
      updatedDoor.handleType = HANDLE_TYPE_DEFAULT;
      updatedDoor.doorOpeningWidth = 990;
      updatedDoor.doorOpeningHeight = 2090;
    }

    if (isAluTypeDoor(updatedDoor)) {
      updatedDoor.doorOpeningWidth = 1000;
    }

    if (isAluGlazyDoor(updatedDoor)) {
      updatedDoor.handleHeight = 1002;
    }

    if (isAluGlazyLeafDoorGlassAround(updatedDoor)) {
      updatedDoor.doorOpeningHeight = 2100;
      updatedDoor.doorGlassType = DOOR_GLASS_1076;
    }

    if (isAluGlazyOnlyLeafDoor(updatedDoor)) {
      updatedDoor.doorOpeningHeight = 2100;
      updatedDoor.doorGlassType = DOOR_GLASS_1076;
      wall.onlyDoor = true;
    }

    if (isOnlyDoorType(updatedDoor)) {
      wall.onlyDoor = true;
    }

    updateDoor(doorId, updatedDoor);
  };

  const handleOnlyDoorCheck = (e: any) => {
    const onlyDoor = getChecked(e);
    const getParts = () => {
      if (!onlyDoor) {
        return wall.parts;
      }

      return wall.parts.map((part: any) => {
        if (part.type !== glassWallPartTypes.door) {
          return part;
        }

        return {
          ...part,
          isFirstDoor: false,
          isLastDoor: false,
        };
      });
    };

    dispatch(
      setWall({
        ...wall,
        onlyDoor,
        parts: getParts(),
      })
    );
  };

  const doorsWithCorrectedPositions = handleFirstAndLastDoorSettings(
    doors,
    wall
  );

  const previousDoor =
    selectedDoor > 0 ? doorsWithCorrectedPositions[selectedDoor - 1] : null;

  const door = doorsWithCorrectedPositions[selectedDoor];

  const errorMessages = validateDoor(door, previousDoor, wall);
  const insertPositionError = getFieldErrorMessage(
    'insertPosition',
    errorMessages
  );

  useEffect(() => {
    if (wall.onlyDoor) {
      handleGlassWallProperties({
        target: {
          name: 'doorType',
          value: door.typeName,
        },
      });
    }
  }, [door.typeName, wall.onlyDoor]);

  useEffect(() => {
    if (wall.onlyDoor) {
      handleGlassWallProperties({
        target: {
          name: 'handleType',
          value: door.handleType,
        },
      });
    }
  }, [door.handleType, wall.onlyDoor]);

  useEffect(() => {
    if (wall.onlyDoor) {
      handleGlassWallProperties({
        target: {
          name: 'handleHeight',
          value: door.handleHeight,
        },
      });
    }
  }, [door.handleHeight, wall.onlyDoor]);

  useEffect(() => {
    if (wall.onlyDoor) {
      handleGlassWallProperties({
        target: {
          name: 'doorHang',
          value: door.doorHang,
        },
      });
    }
  }, [door.doorHang, wall.onlyDoor]);
  useEffect(() => {
    if (wall.onlyDoor) {
      handleGlassWallProperties({
        target: {
          name: 'thresholdType',
          value: door.thresholdType,
        },
      });
    }
  }, [door.thresholdType, wall.onlyDoor]);

  useEffect(() => {
    if (wall.onlyDoor) {
      handleGlassWallProperties({
        target: {
          name: 'woodyThresholdType',
          value: door.woodyThresholdType,
        },
      });
    }
  }, [door.woodyThresholdType, wall.onlyDoor]);

  useEffect(() => {
    handleGlassWallProperties({
      target: {
        name: 'woodyLeftElectricalBoxWidth',
        value: door.woodyLeftElectricalBoxWidth,
      },
    });
  }, [door.woodyLeftElectricalBoxWidth]);

  useEffect(() => {
    handleGlassWallProperties({
      target: {
        name: 'woodyRightElectricalBoxWidth',
        value: door.woodyRightElectricalBoxWidth,
      },
    });
  }, [door.woodyRightElectricalBoxWidth]);

  useEffect(() => {
    handleGlassWallProperties({
      target: {
        name: 'woodyLeftElectricalBox',
        value: door.woodyLeftElectricalBox,
      },
    });
  }, [door.woodyLeftElectricalBox]);

  useEffect(() => {
    handleGlassWallProperties({
      target: {
        name: 'woodyRightElectricalBox',
        value: door.woodyRightElectricalBox,
      },
    });
  }, [door.woodyRightElectricalBox]);

  useEffect(() => {
    handleGlassWallProperties({
      target: {
        name: 'woodyDoorPositionOffset',
        value: door.woodyDoorPositionOffset,
      },
    });
  }, [door.woodyDoorPositionOffset]);

  const getDoorTypes = () => {
    if (isSlidy30(wall)) {
      return doorOptions(['30', 'woodyleafdoor42']);
    }
    if (isSlidy36(wall)) {
      return doorOptions(['36', 'woodyleafdoor42']);
    }
    if (isWoodyTypeWallWith42and43(wall)) {
      return doorOptions(['woody', '42', '43']);
    }
    if (isWoodyTypeWallWithout42and43(wall)) {
      return doorOptions(['woody'], ['43']);
    }
    if (isAluGlazy(wall)) {
      return doorOptions(['slidy', 'alu', 'aluminium', 'woodyleafdoor42']);
    }
    if (is2kAluGlazy(wall)) {
      return doorOptions(['woody', 'alu']);
    }
    return doorOptions();
  };

  if (doors.length === 0) {
    return null;
  }

  return (
    <div key={`glass-wall-door-${door.id}`}>
      <Row>
        <Col>
          <SelectInputFieldControlled
            id="typeName"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Door Type`
            )}
            onChange={handleDoorTypeChange(door.id)}
            name="typeName"
            required
            options={getDoorTypes()}
            value={door.typeName}
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <TextInputField
            id="areaName"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Door Name`
            )}
            onChange={handleDoorProperties(door.id)}
            name="areaName"
            required
            value={door.areaName}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <SelectInputFieldControlled
            id="doorGlassType"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Glass Type`
            )}
            onChange={handleDoorProperties(door.id)}
            name="doorGlassType"
            required
            options={getDoorGlassTypesList()}
            value={door.doorGlassType}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <SelectInputFieldControlled
            id="doorHang"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Door Hang`
            )}
            onChange={handleDoorProperties(door.id)}
            name="doorHang"
            required
            options={doorHangOptions()}
            value={door.doorHang}
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NumberInputField
            id="doorOpeningWidth"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Opening Width`
            )}
            onChange={handleDoorProperties(door.id)}
            value={door.doorOpeningWidth}
            name="doorOpeningWidth"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="doorOpeningHeight"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Opening Height`
            )}
            onChange={handleDoorProperties(door.id)}
            value={door.doorOpeningHeight}
            name="doorOpeningHeight"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <SelectInputFieldControlled
            id="handleType"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Handle Type`
            )}
            onChange={handleDoorProperties(door.id)}
            name="handleType"
            required
            options={handleTypeOptions()}
            value={door.handleType}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="handleHeight"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Handle Height`
            )}
            onChange={handleDoorProperties(door.id)}
            value={door.handleHeight}
            name="handleHeight"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          {!door.isFirstDoor && !door.isLastDoor ? (
            <NumberInputField
              error={insertPositionError}
              id="insertPosition"
              label={t(
                `${translations.glassWallDoorProperties}:fields:Door Position`
              )}
              onChange={handleDoorProperties(door.id)}
              value={door.insertPosition}
              name="insertPosition"
              required
              disabled={disableInputs}
            />
          ) : null}
          {door.isFirstDoor || door.isLastDoor ? (
            <span className="input-error-message">{insertPositionError}</span>
          ) : null}
        </Col>
      </Row>
      <Row>
        <Col className={isSlidy30(wall) || isSlidy36(wall) ? 'hidden' : ''}>
          <SelectInputFieldControlled
            id="doorDirection"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Leaf Door Direction`
            )}
            onChange={handleDoorProperties(door.id)}
            name="doorDirection"
            required
            options={doorLeafDirectionOptions()}
            value={door.doorDirection}
            disabled={disableInputs}
          />
        </Col>
        <Col className={isSlidy30(wall) || isSlidy36(wall) ? 'hidden' : ''}>
          <SelectInputFieldControlled
            id="thresholdType"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Threshold`
            )}
            onChange={handleDoorProperties(door.id)}
            name="thresholdType"
            required
            options={thresholdOptions()}
            value={door.thresholdType}
            disabled={disableInputs}
          />
        </Col>
        <Col className={isSlidy30(wall) || isSlidy36(wall) ? 'hidden' : ''}>
          <NumberInputField
            id="primaryDoorWidth"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Primary Door Width`
            )}
            onChange={handleDoorProperties(door.id)}
            value={door.primaryDoorWidth}
            name="primaryDoorWidth"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      {isWoodyTypeWallWithout42and43(wall) ? (
        <Row>
          <Col>
            <NumberInputField
              id="doorOffsetLeft"
              label={t(
                `${translations.glassWallDoorProperties}:fields:Door Offset Left`
              )}
              onChange={handleGlassWallProperties}
              value={wall.doorOffsetLeft}
              name="doorOffsetLeft"
              disabled={disableInputs}
            />
          </Col>
          <Col>
            <NumberInputField
              id="doorOffsetRight"
              label={t(
                `${translations.glassWallDoorProperties}:fields:Door Offset Right`
              )}
              onChange={handleGlassWallProperties}
              value={wall.doorOffsetRight}
              name="doorOffsetRight"
              disabled={disableInputs}
            />
          </Col>
        </Row>
      ) : null}
      <Row>
        <Col>
          <CheckboxInputField
            id="excludeStopper"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Has stopper`
            )}
            onChange={handleDoorProperties(door.id)}
            value={!door.excludeStopper}
            name="excludeStopper"
            required
            disabled={disableInputs}
          />
        </Col>
        {doors.length === 1 ? (
          <Col>
            <CheckboxInputField
              id="onlyDoor"
              label={t(
                `${translations.glassWallDoorProperties}:fields:Only Door`
              )}
              onChange={handleOnlyDoorCheck}
              value={wall.onlyDoor}
              name="onlyDoor"
              required
              disabled={disableInputs}
            />
          </Col>
        ) : null}
        {selectedDoor === 0 && !wall.onlyDoor ? (
          <Col>
            <CheckboxInputField
              id="isFirstDoor"
              label={t(
                `${translations.glassWallDoorProperties}:fields:Is first door`
              )}
              onChange={handleDoorProperties(door.id)}
              value={door.isFirstDoor}
              name="isFirstDoor"
              required
              disabled={disableInputs}
            />
          </Col>
        ) : null}
      </Row>
      <Row>
        {selectedDoor === doors.length - 1 && !wall.onlyDoor ? (
          <Col>
            <CheckboxInputField
              id="isLastDoor"
              label={t(
                `${translations.glassWallDoorProperties}:fields:Is last door`
              )}
              onChange={handleDoorProperties(door.id)}
              value={door.isLastDoor}
              name="isLastDoor"
              required
              disabled={disableInputs}
            />
          </Col>
        ) : null}
        <Col>
          <CheckboxInputField
            id="hasLeftDoorPrimary"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Left Door Primary`
            )}
            onChange={handleDoorProperties(door.id)}
            value={door.hasLeftDoorPrimary}
            name="hasLeftDoorPrimary"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <CheckboxInputField
            id="hasSolidSlaveDoor"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Has Solid Slave Door`
            )}
            onChange={handleDoorProperties(door.id)}
            value={door.hasSolidSlaveDoor}
            name="hasSolidSlaveDoor"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <hr />
      <DoorGlasses disableInputs={disableInputs} door={door} wall={wall} />
      <hr />
      {door.typeName.includes('woody') ||
      door.typeName.includes('alu') ||
      door.typeName === SLIDY_ONLY_DOOR_OUTSIDE_GLASS_30 ||
      door.typeName === SLIDY_ONLY_DOOR_OUTSIDE_GLASS_36 ? (
        <div>
          <Row>
            <Col>
              <h5>
                {t(
                  `${translations.glassWallDoorProperties}:doorHeaderTitles:aluDoorParameters`
                )}
              </h5>
            </Col>
          </Row>
          <Row>
            <Col>
              <NumberInputField
                id="aluDoorIn2kOffset"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:All door pos offset`
                )}
                onChange={handleWallProperties}
                value={wall.aluDoorIn2kOffset}
                name="aluDoorIn2kOffset"
                required
                disabled={disableInputs}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <NumberInputField
                id="leftSideGlassWidth"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:LeftSideGlassWallWidth`
                )}
                onChange={
                  wall.onlyDoor
                    ? handleWallProperties
                    : handleDoorProperties(door.id)
                }
                value={
                  wall.onlyDoor
                    ? wall.leftSideGlassWidth
                    : door.leftSideGlassWidth
                }
                name="leftSideGlassWidth"
                required
                disabled={disableInputs}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <h5>
                {t(
                  `${translations.glassWallDoorProperties}:doorHeaderTitles:woodyDoorParameters`
                )}
              </h5>
            </Col>
          </Row>
          <Row>
            <Col>
              <NumberInputField
                id="woodyDoorPositionOffset"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:WoodyDoorPosOffset`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyDoorPositionOffset}
                name="woodyDoorPositionOffset"
                required
                disabled={disableInputs}
              />
            </Col>
            {door.typeName.includes('woody') ? (
              <Col>
                <NumberInputField
                  error={getFieldErrorMessage(
                    'woodyDoorFrameDepth',
                    validationErrors
                  )}
                  id="woodyDoorFrameDepth"
                  label={t(
                    `${translations.glassWallDoorProperties}:fields:WoodyDoorFrameDepth`
                  )}
                  onChange={handleDoorProperties(door.id)}
                  value={door.woodyDoorFrameDepth}
                  name="woodyDoorFrameDepth"
                  required
                  disabled={disableInputs}
                />
              </Col>
            ) : null}
          </Row>
          <Row>
            <Col>
              <CheckboxInputField
                id="woodyLeftExtraWoodyProfile"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:LeftProfile`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyLeftExtraWoodyProfile}
                name="woodyLeftExtraWoodyProfile"
                required
              />
            </Col>
            <Col>
              <CheckboxInputField
                id="woodyLeftExtraAluProfile"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:ExtraAluminiumProfile`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyLeftExtraAluProfile}
                name="woodyLeftExtraAluProfile"
                required
              />
            </Col>
            <Col>
              <SelectInputFieldControlled
                id="woodyLeftExtraProfileHeight"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:DoorHeight`
                )}
                name="woodyLeftExtraProfileHeight"
                onChange={handleDoorProperties(door.id)}
                options={woodDoorHeightOptions()}
                required
                value={door.woodyLeftExtraProfileHeight}
                disabled={disableInputs}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <CheckboxInputField
                id="woodyRightExtraWoodyProfile"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:RightProfile`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyRightExtraWoodyProfile}
                name="woodyRightExtraWoodyProfile"
                required
              />
            </Col>
            <Col>
              <CheckboxInputField
                id="woodyRightExtraAluProfile"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:ExtraAluminiumProfile`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyRightExtraAluProfile}
                name="woodyRightExtraAluProfile"
                required
              />
            </Col>
            <Col>
              <SelectInputFieldControlled
                id="woodyRightExtraProfileHeight"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:DoorHeight`
                )}
                name="woodyRightExtraProfileHeight"
                onChange={handleDoorProperties(door.id)}
                options={woodDoorHeightOptions()}
                required
                value={door.woodyRightExtraProfileHeight}
                disabled={disableInputs}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <CheckboxInputField
                id="woodyLeftElectricalBox"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:Left Electrical box`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyLeftElectricalBox}
                name="woodyLeftElectricalBox"
                required
              />
            </Col>
            <Col>
              <NumberInputField
                id="woodyLeftElectricalBoxWidth"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:Width`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyLeftElectricalBoxWidth}
                name="woodyLeftElectricalBoxWidth"
                required
                disabled={disableInputs}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <CheckboxInputField
                id="woodyRightElectricalBox"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:Right Electrical box`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyRightElectricalBox}
                name="woodyRightElectricalBox"
                required
              />
            </Col>
            <Col>
              <NumberInputField
                id="woodyRightElectricalBoxWidth"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:Width`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyRightElectricalBoxWidth}
                name="woodyRightElectricalBoxWidth"
                required
                disabled={disableInputs}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <SelectInputFieldControlled
                id="woodyThresholdType"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:ThresholdOptions`
                )}
                name="woodyThresholdType"
                onChange={handleDoorProperties(door.id)}
                options={woodDoorThresholdOptions()}
                required
                value={door.woodyThresholdType}
                disabled={disableInputs}
              />
            </Col>
            <Col>
              <SelectInputFieldControlled
                id="woodyHingeType"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:HingeOptions`
                )}
                name="woodyHingeType"
                onChange={handleDoorProperties(door.id)}
                options={woodHingeOptions()}
                required
                value={door.woodyHingeType}
                disabled={disableInputs}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <NumberInputField
                id="woodyTotalPortalWidth"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:TotalPortalWallWidth`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyTotalPortalWidth}
                name="woodyTotalPortalWidth"
                required
                disabled={disableInputs}
              />
            </Col>
            <Col>
              <NumberInputField
                id="woodyLeftSidePortalWidth"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:LeftSidePortalWidth`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyLeftSidePortalWidth}
                name="woodyLeftSidePortalWidth"
                required
                disabled={disableInputs}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <NumberInputField
                id="woodyDoorLeftGlassDepth"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:LeftGlassDepth`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyDoorLeftGlassDepth}
                name="woodyDoorLeftGlassDepth"
                required
                disabled={disableInputs}
              />
            </Col>
            <Col>
              <NumberInputField
                id="woodyDoorRightGlassDepth"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:RightGlassDepth`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyDoorRightGlassDepth}
                name="woodyDoorRightGlassDepth"
                required
                disabled={disableInputs}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <NumberInputField
                id="woodyDoorLeftGrooveDepth"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:LeftGrooveDepth`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyDoorLeftGrooveDepth}
                name="woodyDoorLeftGrooveDepth"
                required
                disabled={disableInputs}
              />
            </Col>
            <Col>
              <NumberInputField
                id="woodyDoorRightGrooveDepth"
                label={t(
                  `${translations.glassWallDoorProperties}:fields:RightGrooveDepth`
                )}
                onChange={handleDoorProperties(door.id)}
                value={door.woodyDoorRightGrooveDepth}
                name="woodyDoorRightGrooveDepth"
                required
                disabled={disableInputs}
              />
            </Col>
          </Row>
        </div>
      ) : null}
    </div>
  );
};

export default SelectedDoorProperties;
